import { apiFetch, REACT_APP_API_ROOT } from "../utilities/api";
import { MediaItemDetailed, RelatedEntityItem, extractResultsFromResponse, ApiResponse } from "../types";
import { getMediaTypeFromString } from "../utilities/mediaUtils";

/**
 * Transforms relationship keys from the table format to the API format.
 *
 * The table uses tableName (e.g., "trips", "specialthings", "simpletags")
 * but the API expects relationshipKey (e.g., "trips", "special_things", "simple_tags").
 * This applies to both related_entities and geo_entities.
 *
 * @param relationships - The relationships object from the table
 * @param config - The application config containing API endpoints
 * @returns The transformed relationships object
 */
export const transformRelationships = (
  relationships: MediaItemDetailed["relationships"],
  config: any
): MediaItemDetailed["relationships"] => {
  if (!relationships) {
    return { related_entities: {}, geo_entities: {}, media: [] };
  }

  // Map tableName to relationshipKey for API writes (V2: tableName is the canonical key)
  const returnedToRelationshipKeyMap = config.apiEndpoints.reduce(
    (map: Record<string, string>, endpoint: any) => {
      if (endpoint.tableName && endpoint.relationshipKey) {
        map[endpoint.tableName] = endpoint.relationshipKey;
      }
      return map;
    },
    {} as Record<string, string>
  );

  // Transform both related_entities and geo_entities keys
  const result = {
    related_entities: {} as Record<string, any[]>,
    geo_entities: {} as Record<string, any[]>,
    media: [] as any[]
  };

  // Transform related_entities
  if (relationships.related_entities) {
    for (const [returnedKey, entities] of Object.entries(relationships.related_entities)) {
      // Find the correct API key for this entity type
      const relationshipKey = returnedToRelationshipKeyMap[returnedKey] || returnedKey;

      // Create cleaned entity arrays with only the fields the backend needs
      const cleanedEntities = entities.map((entity: RelatedEntityItem) => ({
        id: Number(entity.id),
        rel_description: entity.rel_description || "Associated through editor",
        is_primary: Boolean(entity.is_primary)
      }));

      result.related_entities[relationshipKey] = cleanedEntities;
    }
  }

  // Transform geo_entities
  if (relationships.geo_entities) {
    for (const [returnedKey, entities] of Object.entries(relationships.geo_entities)) {
      // Find the correct API key for this entity type
      const relationshipKey = returnedToRelationshipKeyMap[returnedKey] || returnedKey;

      // Create cleaned entity arrays with only the fields the backend needs
      const cleanedEntities = entities.map((entity: RelatedEntityItem) => ({
        id: Number(entity.id),
        rel_description: entity.rel_description || "Associated through editor",
        is_primary: Boolean(entity.is_primary)
      }));

      result.geo_entities[relationshipKey] = cleanedEntities;
    }
  }

  // Transform media relationships
  if (relationships.media && relationships.media.length > 0) {
    result.media = relationships.media.map((entity: RelatedEntityItem) => ({
      id: Number(entity.id),
      rel_description: entity.rel_description || "Associated through editor",
      is_primary: Boolean(entity.is_primary)
    }));
  }

  return result;
};

/**
 * Fetches media items by their IDs with fresh data (no caching)
 *
 * @param ids - Array of media item IDs to fetch
 * @param onProgress - Optional callback for progress updates
 * @param onError - Optional callback for error handling
 * @returns Promise resolving to array of fetched media items
 */
export const fetchMediaItemsByIds = async (
  ids: string[],
  onProgress?: (progress: number, total: number) => void,
  onError?: (id: string, error: any) => void
): Promise<MediaItemDetailed[]> => {
  if (ids.length === 0) return [];

  const allItems: MediaItemDetailed[] = [];
  const maxBatchSize = 100; // Maximum number of IDs to include in a single request

  console.log(`Starting to fetch ${ids.length} specific media items...`);

  try {
    // Use batch request when possible for efficiency
    if (ids.length <= maxBatchSize) {
      // Add debug logging for the API URL construction
      const queryString = new URLSearchParams({
        ids: ids.join(","),
        include_rels: "true",
        _cache: Date.now().toString(), // Prevent caching
        limit: ids.length.toString()
      }).toString();

      const url = `${REACT_APP_API_ROOT}api/user/media/?${queryString}`;
      console.log(`Making batch request: ${url}`);

      const response = (await apiFetch(url, { method: "GET" })) as ApiResponse<any>;
      console.log("Raw API response:", response);

      // Use the utility function from types.ts to extract results
      const responseArray = extractResultsFromResponse(response);
      console.log(`Received batch response with ${responseArray.length} items`);

      // Process and add each item
      responseArray.forEach((item: any) => {
        // Convert the item to the expected MediaItemDetailed format
        const mediaType = getMediaTypeFromString(item.media_type || "");

        const formattedItem: MediaItemDetailed = {
          ...item,
          id: String(item.id),
          fileName: item.original_file?.filename || "Unknown filename",
          type: mediaType
        };

        allItems.push(formattedItem);
      });

      if (onProgress) {
        onProgress(ids.length, ids.length);
      }
    } else {
      // For larger sets, process in smaller batches
      for (let i = 0; i < ids.length; i += maxBatchSize) {
        const batchIds = ids.slice(i, i + maxBatchSize);
        const queryString = new URLSearchParams({
          ids: batchIds.join(","),
          include_rels: "true",
          _cache: Date.now().toString(),
          limit: batchIds.length.toString()
        }).toString();

        const url = `${REACT_APP_API_ROOT}api/user/media/?${queryString}`;
        console.log(`Making batch request ${i / maxBatchSize + 1}/${Math.ceil(ids.length / maxBatchSize)}: ${url}`);

        const response = (await apiFetch(url, { method: "GET" })) as ApiResponse<any>;

        // Use the utility function from types.ts to extract results
        const responseArray = extractResultsFromResponse(response);
        console.log(`Received batch ${i / maxBatchSize + 1} response with ${responseArray.length} items`);

        // Process and add each item from this batch
        responseArray.forEach((item: any) => {
          // Convert the item to the expected MediaItemDetailed format
          const mediaType = getMediaTypeFromString(item.media_type || "");

          const formattedItem: MediaItemDetailed = {
            ...item,
            id: String(item.id),
            fileName: item.original_file?.filename || "Unknown filename",
            type: mediaType
          };

          allItems.push(formattedItem);
        });

        if (onProgress) {
          onProgress(i + batchIds.length, ids.length);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching media items in batch:", error);

    // If batch request fails, fall back to individual fetches
    console.log("Falling back to individual fetch requests...");

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];

      try {
        const itemUrl = `${REACT_APP_API_ROOT}api/user/media/${id}/?include_rels=true&_cache=${Date.now()}`;
        const itemResponse = await apiFetch(itemUrl, { method: "GET" });

        if (itemResponse) {
          const mediaType = getMediaTypeFromString(itemResponse.media_type || "");

          const formattedItem: MediaItemDetailed = {
            ...itemResponse,
            id: String(itemResponse.id),
            fileName: itemResponse.original_file?.filename || "Unknown filename",
            type: mediaType
          };

          allItems.push(formattedItem);
        }

        if (onProgress) {
          onProgress(i + 1, ids.length);
        }
      } catch (itemError) {
        console.error(`Error fetching individual item ${id}:`, itemError);
        if (onError) {
          onError(id, itemError);
        }
      }
    }
  }

  console.log(`Completed fetching ${allItems.length} of ${ids.length} requested items`);

  // Log the first item to help debug what's returned
  if (allItems.length > 0) {
    console.log("Sample item structure:", {
      hasRelationships: !!allItems[0].relationships,
      relationshipKeys: allItems[0].relationships ? Object.keys(allItems[0].relationships) : [],
      itemKeys: Object.keys(allItems[0])
    });
  }

  return allItems;
};

/**
 * Updates a media item's name and description (relationships are managed separately)
 *
 * @param item - The media item to update
 * @returns Promise resolving to the updated item
 */
export const updateMediaItem = async (item: MediaItemDetailed): Promise<MediaItemDetailed> => {
  const itemId = String(item.id);

  // Send only name/description — relationships are handled via a dedicated endpoint
  await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${itemId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: item.name,
      description: item.description ?? ""
    })
  });

  // Fetch the updated item to ensure we have fresh data
  const timestamp = new Date().getTime();
  const updatedItemResponse = await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${itemId}/?include_relations=true&_cache=${timestamp}`, {
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache"
    }
  });

  // Convert MIME type to our frontend media type format
  const mediaType = getMediaTypeFromString(updatedItemResponse.media_type || "");

  // Format the response
  const formattedItem = {
    ...updatedItemResponse,
    id: String(updatedItemResponse.id),
    fileName: updatedItemResponse.original_file?.filename || "Unknown filename",
    // Set the type field using the converted media type
    type: mediaType
  };

  return formattedItem;
};
